export type IterationOrder = 'top-bottom' | 'bottom-top' | 'random' | 'random-by-line';
export type NeighborOrder = 'default' | 'random' | 'clockwise' | 'counter-clockwise';

/**
 * Generic 2D grid that stores data in a Float32Array.
 * Provides basic operations for accessing and manipulating grid data.
 */
export class Grid2D {
  protected data: Float32Array;
  public readonly width: number;
  public readonly height: number;

  constructor(width: number, height: number, initialValue: number = 0) {
    this.width = width;
    this.height = height;
    this.data = new Float32Array(width * height);

    if (initialValue !== 0) {
      this.fill(initialValue);
    }
  }

  /**
   * Get the flat array index for coordinates (x, y).
   */
  public getIndex(x: number, y: number): number {
    return y * this.width + x;
  }

  public isValid(x: number, y: number): boolean {
    return x >= 0 && x < this.width && y >= 0 && y < this.height;
  }

  public get(x: number, y: number): number {
    if (!this.isValid(x, y)) {
      return 0;
    }
    return this.data[this.getIndex(x, y)];
  }

  public set(x: number, y: number, value: number): void {
    if (!this.isValid(x, y)) {
      return;
    }
    this.data[this.getIndex(x, y)] = Math.max(0, value);
  }

  public add(x: number, y: number, delta: number): void {
    this.set(x, y, this.get(x, y) + delta);
  }

  public fill(value: number): void {
    this.data.fill(value);
  }

  public clear(): void {
    this.data.fill(0);
  }

  public getData(): Float32Array {
    return this.data;
  }

  /**
   * Get the 4-connected neighbors of a cell (up, down, left, right).
   * @param order The order in which to return neighbors
   */
  public getNeighbors(
    x: number,
    y: number,
    order: NeighborOrder = 'default'
  ): Array<{ x: number; y: number; value: number }> {
    const neighbors: Array<{ x: number; y: number; value: number }> = [];

    let offsets = [
      { dx: -1, dy: 0 },  // left
      { dx: 1, dy: 0 },   // right
      { dx: 0, dy: -1 },  // up
      { dx: 0, dy: 1 }    // down
    ];

    // Reorder based on order parameter
    if (order === 'clockwise') {
      offsets = [
        { dx: 0, dy: -1 },  // up
        { dx: 1, dy: 0 },   // right
        { dx: 0, dy: 1 },   // down
        { dx: -1, dy: 0 }   // left
      ];
    } else if (order === 'counter-clockwise') {
      offsets = [
        { dx: 0, dy: -1 },  // up
        { dx: -1, dy: 0 },  // left
        { dx: 0, dy: 1 },   // down
        { dx: 1, dy: 0 }    // right
      ];
    } else if (order === 'random') {
      // Fisher-Yates shuffle
      for (let i = offsets.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [offsets[i], offsets[j]] = [offsets[j], offsets[i]];
      }
    }

    for (const { dx, dy } of offsets) {
      const nx = x + dx;
      const ny = y + dy;

      if (this.isValid(nx, ny)) {
        neighbors.push({
          x: nx,
          y: ny,
          value: this.get(nx, ny)
        });
      }
    }

    return neighbors;
  }

  /**
   * Find the maximum value in the grid.
   */
  public getMax(): number {
    let max = 0;
    for (let i = 0; i < this.data.length; i++) {
      if (this.data[i] > max) {
        max = this.data[i];
      }
    }
    return max;
  }

  /**
   * Find the minimum value in the grid.
   */
  public getMin(): number {
    let min = Infinity;
    for (let i = 0; i < this.data.length; i++) {
      if (this.data[i] < min) {
        min = this.data[i];
      }
    }
    return min === Infinity ? 0 : min;
  }

  /**
   * Calculate the sum of all values in the grid.
   */
  public getSum(): number {
    let sum = 0;
    for (let i = 0; i < this.data.length; i++) {
      sum += this.data[i];
    }
    return sum;
  }

  /**
   * Apply a callback function to each cell in the grid.
   * @param callback Function to call for each cell (x, y, value)
   * @param order The order in which to iterate through cells
   */
  public apply(
    callback: (x: number, y: number, value: number) => void,
    order: IterationOrder = 'top-bottom'
  ): void {
    if (order === 'top-bottom') {
      for (let y = 0; y < this.height; y++) {
        for (let x = 0; x < this.width; x++) {
          callback(x, y, this.get(x, y));
        }
      }
    } else if (order === 'bottom-top') {
      for (let y = this.height - 1; y >= 0; y--) {
        for (let x = 0; x < this.width; x++) {
          callback(x, y, this.get(x, y));
        }
      }
    } else if (order === 'random') {
      // Create array of all indices and shuffle
      const indices: Array<{ x: number; y: number }> = [];
      for (let y = 0; y < this.height; y++) {
        for (let x = 0; x < this.width; x++) {
          indices.push({ x, y });
        }
      }
      // Fisher-Yates shuffle
      for (let i = indices.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [indices[i], indices[j]] = [indices[j], indices[i]];
      }
      // Apply in shuffled order
      for (const { x, y } of indices) {
        callback(x, y, this.get(x, y));
      }
    } else if (order === 'random-by-line') {
      // Process each row from top to bottom, but randomize x within each row
      for (let y = 0; y < this.height; y++) {
        // Create array of x indices for this row
        const xIndices: number[] = [];
        for (let x = 0; x < this.width; x++) {
          xIndices.push(x);
        }
        // Shuffle x indices
        for (let i = xIndices.length - 1; i > 0; i--) {
          const j = Math.floor(Math.random() * (i + 1));
          [xIndices[i], xIndices[j]] = [xIndices[j], xIndices[i]];
        }
        // Apply in shuffled x order
        for (const x of xIndices) {
          callback(x, y, this.get(x, y));
        }
      }
    }
  }
}
